<template>
  <div class="component-drawing">
    <div v-show="loading" class="loading-overlay">
      <p>Adding Page, please wait...</p>
    </div>
    <div class="sticky-parent">
      <div id="tx-drawing-buttons" class="sticky-top mt-2 mr-2">
        <b-button-group class="mr-3" size="sm">
          <b-button
            class="px-auto mx-auto width-45px"
            variant="outline-primary"
            :pressed="!eraserMode"
            @click="eraserMode = false"
            @touchstart="eraserMode = false"
          >
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button
            class="mx-auto width-45px"
            variant="outline-info"
            :pressed="eraserMode"
            @click="eraserMode = true"
            @touchstart="eraserMode = true"
          >
            <i class="fas fa-eraser"></i>
          </b-button>
        </b-button-group>

        <b-button-group class="mr-3" size="sm">
          <VSwatches v-model="color" popover-x="right">
            <b-button
              class="px-auto mx-auto width-45px rounded-left"
              slot="trigger"
              size="sm"
              :style="
                'background-color:' +
                color +
                ';cursor: pointer; height: 31px;border-radius: 0px'
              "
              @click="eraserMode = false"
              ><i class="fas fa-palette"></i
            ></b-button>
          </VSwatches>
          <b-button
            id="btn-thickness"
            class="width-45px"
            variant="light"
            size="sm"
            @click="eraserMode = false"
          >
            <hr
              class="m-auto border-0 rounded"
              :style="{
                height: thickness + 'px',
                backgroundColor: color,
              }"
            />
          </b-button>
        </b-button-group>
        <b-button-group class="mr-3" size="sm">
          <b-button
            variant="primary"
            :disabled="historyStep <= 0"
            @click="undoStroke"
          >
            <i class="fas fa-undo"></i>
          </b-button>
          <b-button
            variant="info"
            :disabled="historyStep === historyLines.length"
            @click="redoStroke"
          >
            <i class="fas fa-redo"></i>
          </b-button>
        </b-button-group>
        <b-button-group class="mr-3" size="sm">
          <b-button variant="warning" @click="loadLinesFromLocalStorage">
            <i class="fas fa-paste"></i>
          </b-button>
        </b-button-group>

        <b-button class="mr-2" size="sm" @click="clearLines" variant="danger">
          <i class="fas fa-trash-alt"></i>
        </b-button>
      </div>
    </div>
    <div class="overflow-hidden" v-for="(page, index) in pages" :key="index">
      <div class="page-container" style="position: relative;">
        <v-stage
          :id="'stage' + index"
          :ref="'stage' + index"
          :config="getconfigKonva(index)"
          @mousedown="mouseDrawing($event, index)"
          @touchstart="pencilDrawing($event, index)"
          @mouseup="stopDrawing($event, index)"
          @touchend="stopDrawing($event, index)"
          @mousemove="drawing($event, index)"
          @touchmove="drawing($event, index)"
          @mouseleave="handleLeave($event, index)"
          @touchleave="handleLeave($event, index)"
          @pointerleave="handleLeave($event, index)"
        >
          <v-layer>
            <v-rect :config="page.configBackground"></v-rect>
            <v-text :config="page.configText"></v-text>
            <v-line
              v-for="(line, lineindex) in lines[index]"
              :key="`page-${index}-${lineindex}`"
              :config="line"
            ></v-line>
          </v-layer>
        </v-stage>

        <!-- Floating page control panel -->
        <div class="page-control-panel">
          <div class="page-number">Page {{ index + 1 }}</div>
          <div class="page-buttons">
            <b-button 
              size="sm" 
              variant="outline-primary" 
              @click="addNewPage"
              class="page-control-btn add-page-btn"
              title="Add Page">
              <i class="fas fa-plus"></i>
            </b-button>
            <b-button 
              v-if="pages.length > 1"
              size="sm" 
              variant="outline-danger" 
              @click="deletePageNumber(index)"
              class="page-control-btn delete-page-btn"
              title="Delete Page">
              <i class="fas fa-trash"></i>
            </b-button>
          </div>
        </div>
      </div>

      <br /><br />
    </div>

    <b-popover
      id="popoverThickness"
      class="p-0"
      target="btn-thickness"
      placement="bottom"
      triggers="click blur"
    >
      <b-button-group>
        <b-button
          variant="light"
          :pressed="thickness == 0.6"
          @click="(thickness = 0.6), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '1px',
              width: '1px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 1.2"
          @click="(thickness = 1.2), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '2px',
              width: '2px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 2"
          @click="(thickness = 2), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '3px',
              width: '3px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 4"
          @click="(thickness = 4), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '4px',
              width: '4px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 9"
          @click="(thickness = 9), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '9px',
              width: '9px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 14"
          @click="(thickness = 14), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '14px',
              width: '14px',
            }"
          ></span
        ></b-button>
        <b-button
          variant="light"
          :pressed="thickness == 20"
          @click="(thickness = 20), hidePopover()"
          ><span
            class="spanDot"
            :style="{
              height: '20px',
              width: '20px',
            }"
          ></span
        ></b-button>
      </b-button-group>
    </b-popover>
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import noteTemplate from "@/assets/img/noteTemplate.jpeg";

import VSwatches from "vue-swatches";
import "vue-swatches/dist/vue-swatches.css";

import { mapGetters } from "vuex";
import moment from "moment";

import { eventBus } from "@/main";
import Dialog from "@/components/modal/Dialog";
import DrawingMixin from "@/mixins/DrawingMixin";

import "@/theme/modal/PaymentHistory/Drawing.scss";

export default {
  name: "Drawing",
  components: {
    VSwatches,
    Dialog,
  },
  mixins: [DrawingMixin],
  props: ["patient", "drawNote", "summaryTableBusy"],
  data() {
    return {
      configKonva: {
        width: 720,
        height: 960,
      },
      configBackground: {
        x: 0,
        y: 0,
        width: 720,
        height: 960,
        fillPatternImage: null,
        preventDefault: false,
      },
      configText: {
        x: 15,
        y: 10,
        text: this.drawNote?.text || "ไม่มึข้อมูลบันทึก",
        fontSize: 14,
        lineHeight: 1.5,
        fill: "#3D556E",
      },
      pages: [],
      lastSavedDrawNote: { text: {}, lines: [] },

      thickness: 2,
      color: "#2980B9",

      // Efficient history management
      historyManager: null,
      
      // Fallback history for undo/redo (temporary solution)
      fallbackHistoryLines: [],
      fallbackHistoryStep: 0,

      // History properties for undo/redo functionality
      historyLines: [],
      historyStep: 0,

      //Konva objects
      stage: null,

      loading: false,
      drawNoteInitialized: false,
      
      // Performance monitoring
      performanceDebugMode: false,
    };
  },
  watch: {
    drawNote: {
      handler() {
        this.$nextTick(() => {
          if (this.drawNote) {
            // DrawNote updated
          } else {
            // drawNote is empty or undefined
          }
        });
      },
      deep: true, // Use deep watching for nested objects/arrays
    },
  },
  computed: {
    ...mapGetters({
      getUserInfo: "moduleUser/getUserInfo",
    }),
    newDrawNote() {
      return {
        text: this.configText.text || "",
        // Drawing on a page whose index is past lines.length leaves holes in the
        // array (Vue's $set grows it sparsely), and holes serialise to null,
        // which the treatment API rejects. Fill them with empty pages instead.
        lines: Array.isArray(this.lines)
          ? Array.from(this.lines, page => page || [])
          : [],
      };
    },
  },
  methods: {
    //drawing
    mouseDrawing(e, pageIndex) {
      if (this.eraserMode) {
        this.startObjEraser(e, pageIndex);
      } else {
        this.startDrawing(e, pageIndex);
      }
    },

    getconfigKonva() {
      const baseConfig = {
        width: this.configKonva.width,
        height: this.configKonva.height,
      };
      return this.getDrawingStageConfig(baseConfig);
    },
    pencilDrawing(e, pageIndex) {
      
      // Check if it's Apple Pencil per-event using the mixin method
      const isApplePencil = this.detectApplePencilFromTouch(e);
      
      
      if (this.eraserMode) {
        // Only start eraser for Apple Pencil touches
        if (isApplePencil) {
          // Prevent scrolling for Apple Pencil in eraser mode
          e.evt.preventDefault();
          this.startObjEraser(e, pageIndex);
        }
        // For finger touches in eraser mode, do nothing (allow scrolling)
      } else {
        // For drawing mode, handle both Apple Pencil and finger touches
        this.handleTouchStart(e, pageIndex);
      }
    },
    startDrawing(e, pageIndex) {
      // Ensure we are not in eraser mode
      if (!this.eraserMode) {
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();

        if (!pos) return;

        this.isDrawing = true;
        this.currentDrawing = {
          ...this.drawingProps,
          stroke: this.color,
          strokeWidth: this.thickness,
          points: [pos.x, pos.y],
        };

        // Ensure lines array exists for this page
        if (!this.lines[pageIndex]) {
          this.$set(this.lines, pageIndex, []);
        }

        this.lines[pageIndex].push(this.currentDrawing);
        this.scheduleBatchDraw();
      }
    },

    stopDrawing(_, pageIndex) {
      
      if (!this.isDrawing) {
        return;
      }

      if (this.eraserMode) {
        if (this.lines[pageIndex] && this.lines[pageIndex].length > 0) {
          this.lines[pageIndex].pop();
        }
        if (this.linesEdited) {
          this.addHistory();
          this.linesEdited = false;
        }
      } else {
        this.finishDrawing();
        this.strokeCount++; // Increment stroke count for history throttling
        this.addHistory();
      }
      
      this.$emit("update:drawNote", this.newDrawNote);
      
    },

    drawing(e, pageIndex) {
      
      if (!this.isDrawing) {
        return;
      }

      if (this.eraserMode) {
        // Only handle erasing for Apple Pencil touches
        const isApplePencil = this.detectApplePencilFromTouch(e);
        
        if (isApplePencil) {
          // Prevent scrolling for Apple Pencil in eraser mode
          e.evt.preventDefault();
          this.continueDrawing(e, pageIndex);
          this.objErasing(pageIndex);
        }
        return;
      }
      
      this.continueDrawing(e, pageIndex);
    },

    handleLeave(e, pageIndex) {
      // Use the appropriate leave handler from the mixin based on the event type
      if (e.evt && e.evt.pointerType === 'pen') {
        this.handleTouchLeave(e, pageIndex);
      } else if (e.evt && e.evt.pointerType === 'touch') {
        this.handleTouchLeave(e, pageIndex);
      } else {
        this.handleMouseLeave(e, pageIndex);
      }
      // Also emit the draw note update when leaving
      this.$emit("update:drawNote", this.newDrawNote);
    },

    //undo
    undoStroke() {
      if (this.historyStep <= 0) return;
      this.historyStep -= 1;
      this.lines = this.historyLines[this.historyStep - 1] || [];
    },
    redoStroke() {
      if (this.historyStep === this.historyLines.length) return;

      this.historyStep += 1;
      this.lines = this.historyLines[this.historyStep - 1] || [];
    },
    addHistory() {
      // Throttle history updates to reduce JSON serialization frequency
      if (!this.shouldAddHistory()) {
        return;
      }
      
      this.historyLines = this.historyLines.slice(0, this.historyStep);
      
      // Limit history size to prevent memory bloat and reduce serialization cost
      const MAX_HISTORY_SIZE = 10; // Reduced from 50 to 10 for better performance
      if (this.historyLines.length >= MAX_HISTORY_SIZE) {
        // Remove oldest entries when limit is reached
        this.historyLines.splice(0, this.historyLines.length - MAX_HISTORY_SIZE + 1);
        this.historyStep = Math.min(this.historyStep, this.historyLines.length);
      }
      
      // Deep clone the nested array - this is expensive but necessary
      this.historyLines.push(JSON.parse(JSON.stringify(this.lines)));
      this.historyStep += 1;
      this.lastHistoryTime = Date.now();
    },

    shouldAddHistory() {
      // Throttle history updates to every 2 seconds or every 5th stroke to reduce performance impact
      const now = Date.now();
      const timeSinceLastHistory = now - (this.lastHistoryTime || 0);
      const strokesSinceLastHistory = (this.strokeCount || 0) - (this.lastHistoryStrokeCount || 0);
      
      if (timeSinceLastHistory > 2000 || strokesSinceLastHistory >= 5) {
        this.lastHistoryStrokeCount = this.strokeCount || 0;
        return true;
      }
      return false;
    },

    //clear
    clearLines() {
      this.$refs.Dialog.showAlertConfirm(
        'ลบการเขียนทั้งหมด"',
        "คุณต้องการลบข้อมูลการเขียนทั้งหมด?"
      ).then(res => {
        if (res.isConfirmed) {
          this.lines = [];
          this.addHistory();
        }
      });
    },

    async insertText() {
      if (this.drawNote.text) return;
      let patientFirstName;
      let patientLastName;
      patientFirstName = this.patient?.firstNameTh || "";
      patientLastName = this.patient?.lastNameTh || "";

      let userName =
        this.getUserInfo.firstNameTh + " " + this.getUserInfo.lastNameTh;
      let dateTimeRecord = moment().locale("th").format("D MMM YY H:mm");
      this.configText.text =
        dateTimeRecord +
        "\nชื่อคนไข้: " +
        patientFirstName +
        " " +
        patientLastName +
        "\nผู้บันทึก: " +
        userName;
      // Redraw all stages
      Object.keys(this.$refs).forEach(refKey => {
        if (refKey.startsWith("stage")) {
          const vueStage = this.$refs[refKey][0];
          if (vueStage && vueStage.getStage) {
            const stage = vueStage.getStage();
            if (stage) {
              stage.batchDraw();
            }
          }
        }
      });
      eventBus.$emit("drawNoteUpdated", this.drawNote);
    },

    defaultValue() {
      this.lines = [];
      this.configText = {
        x: 15,
        y: 10,
        text: "",
        fontSize: 14,
        lineHeight: 1.5,
        fill: "#3D556E",
      };
      this.historyManager.reset();
      this.fallbackHistoryLines = [];
      this.fallbackHistoryStep = 0;
      this.historyLines = [];
      this.historyStep = 0;
      this.lastSavedDrawNote = {};
      this.eraserMode = false;
    },

    hidePopover() {
      this.$root.$emit("bv::hide::popover", "popoverThickness");
    },

    retrieveDrawNote() {
      eventBus.$emit("returnDrawNote", this.drawNote);
      this.defaultValue();
    },
    assignDrawNote(e) {
      if (e?.lines.length > 0) {
        this.configText.text = e.text;
        this.lines = [...e.lines];
      }
    },
    saveLinesToLocalStorage() {
      if (this.lines?.length === 0) return;
      localStorage.setItem("drawingLines", JSON.stringify(this.lines));
    },
    async addNewPage() {
      if (this.pages.length >= 5) {
        this.$bvToast.toast("Maximum of 5 pages reached.", {
          title: "Page Limit Reached",
          variant: "warning",
          solid: true,
          autoHideDelay: 3000,
          toaster: "b-toaster-bottom-left",
        });
        return; // Exit the method if the page limit is reached
      }
      this.loading = true;
      const newPage = {
        configBackground: {
          x: 0,
          y: 0,
          width: 720,
          height: 960,
          fillPatternImage: null,
          preventDefault: false,
        },
        configText: {
          x: 15,
          y: 10,
          text: this.configText.text || "", // Use drawNote to set text
          fontSize: 14,
          lineHeight: 1.5,
          fill: "#3D556E",
        },
        lines: [],
      };

      //Add the new page to the pages array
      this.pages.push(newPage);

      try {
        const newPageIndex = this.pages.length - 1;
        await this.$nextTick(); // Ensure the page is rendered

        await this.loadBackgroundTemplate(newPageIndex); // Wait for the background to load
      } catch (error) {
        // Error loading background
      } finally {
        this.loading = false; // Stop loading regardless of success or error
      }

    },
    deletePage() {
      if (this.pages.length <= 1) {
        // Show a toast message informing the user that they can't delete the last page
        this.$bvToast.toast("You cannot delete the last page.", {
          title: "Delete Page",
          variant: "warning",
          solid: true,
          autoHideDelay: 3000,
        });
        return; // Exit the function if there is only one page left
      }
      // Confirm deletion with a toast message
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete last page?", {
          title: "Delete Page",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          footerClass: "p-2",
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) {
            if (this.drawNote && this.drawNote.lines) {
              // First, check if it's null, and set it to an empty array
              if (this.drawNote.lines === null) {
                this.drawNote.lines = []; // Set to empty array if null
              }

              // Now check if it's an array and has elements
              if (
                Array.isArray(this.drawNote.lines) &&
                this.drawNote.lines.length > 0
              ) {
                this.drawNote.lines.pop(); // Pop the last line if not empty
              }
            }
            // Remove the page from pages array
            this.pages.pop();

            this.deleteSavePages();

            // Show success toast notification
            this.$bvToast.toast(
              `Page ${this.pages.length + 1} deleted successfully.`,
              {
                title: "Page Deleted",
                variant: "info",
                solid: true,
                autoHideDelay: 3000,
                toaster: "b-toaster-bottom-left",
              }
            );
          }
        })
        .catch(() => {});
    },


    deletePageNumber(index) {
      if (this.pages.length <= 1) {
        // Show a toast message informing the user that they can't delete the last page
        this.$bvToast.toast("You cannot delete the last page.", {
          title: "Delete Page",
          variant: "warning",
          solid: true,
          autoHideDelay: 3000,
        });
        return; // Exit the function if there is only one page left
      }
      this.$bvModal
        .msgBoxConfirm(`Are you sure you want to delete Page ${index + 1}?`, {
          title: "Delete Page",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          footerClass: "p-2",
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) {
            if (this.drawNote && this.drawNote.lines) {
              if (this.drawNote.lines === null) {
                this.drawNote.lines = [];
              }

              if (Array.isArray(this.drawNote.lines[index])) {
                this.drawNote.lines.splice(index, 1);
              }
            }

            //Remove page from the pages array
            if (Array.isArray(this.pages) && index < this.pages.length) {
              this.pages.splice(index, 1);
            }

            //show success toast notification
            this.$bvToast.toast(`Page ${index + 1} deleted successfully`, {
              title: "Page Deleted",
              variant: "info",
              solid: true,
              autoHideDelay: 3000,
              toaster: "b-toaster-bottom-left",
            });
          }
        })
        .catch(() => {});
    },


    saveLines() {
      this.pages.forEach((_, index) => {
        const pageLines = this.lines[index]; // Get the lines for the current page
        if (pageLines && pageLines.length > 0) {
          // Save lines for the current page in localStorage
          localStorage.setItem(
            `savedLines-${index}`,
            JSON.stringify(pageLines)
          );
        }
      });
    },

    deleteSaveLines(lastPageIndex) {
      // Remove saved lines for the last page from localStorage
      localStorage.removeItem(`savedLines-${lastPageIndex}`);
    },

    savePages() {
      // Retrieve the current count from localStorage, defaulting to 0 if it doesn't exist
      let pageCount = parseInt(localStorage.getItem("savedPageCount"), 10) || 0;

      // Increment the page count by 1
      pageCount += 1;

      // Save the updated count to localStorage
      localStorage.setItem("savedPageCount", pageCount);
    },

    deleteSavePages() {
      let pageCount = parseInt(localStorage.getItem("savedPageCount"), 10) || 0;

      pageCount = Math.max(0, pageCount - 1);

      localStorage.setItem("savedPageCount", pageCount);
    },

    loadBackgroundTemplate(index) {
      return new Promise((resolve, reject) => {
        const stageRefKey = `stage${index}`;
        const stageRef = this.$refs[stageRefKey]?.[0];

        if (stageRef) {
          const stage = stageRef.getNode();

          // Load and assign the noteTemplate image as the background
          const image = new window.Image();
          image.src = noteTemplate;
          image.onload = () => {
            this.pages[index].configBackground.fillPatternImage = image;
            stage.draw();
            resolve(); // Resolve the promise when the image is loaded successfully
          };
          image.onerror = () => {
            reject(new Error(`Failed to load image for stage ${stageRefKey}`)); // Reject if there’s an error
          };
        } else {
          reject(
            new Error(
              `Stage reference ${stageRefKey} not found in loadBackgroundTemplate`
            )
          ); // Reject if stage is not found
        }
      });
    },

    async loadLinesFromLocalStorage() {
      let lines = localStorage.getItem("drawingLines") || "";
      if (lines) {
        let res = await this.$refs.Dialog.showAlertConfirm(
          "Paste การเขียนล่าสุด",
          "ใช้ในกรณีระบบมีปัญหา เขียนไปแล้ว แต่ไม่สามารถบันทึกลงประวัติคนไข้ ระบบจะดึงการเขียนล่าสุดกลับมา"
        );
        if (res.isConfirmed) this.lines = JSON.parse(lines);
      } else {
        this.$refs.Dialog.showAlertInfo("ไม่มีข้อมูลกรเขียนที่ถูกบันทึกไว้");
      }
    },

    async initializePages() {
      this.pages = [];
      const savedPagesCount =
        this.drawNote.lines && this.drawNote.lines.length > 0
          ? this.drawNote.lines.length
          : 0;

      if (savedPagesCount === 0) {
        // If no saved pages, initialize with one default page
        this.pages.push({
          configBackground: {
            x: 0,
            y: 0,
            width: 720,
            height: 960,
            fillPatternImage: null,
            preventDefault: false,
          },
          configText: {
            x: 15,
            y: 10,
            text: this.configText.text || "", // Use drawNote to set text
            fontSize: 14,
            lineHeight: 1.5,
            fill: "#3D556E",
          },
          lines: [],
        });
        this.lines.push([]);

        const numPages = 1;
        this.$nextTick(() => {
          for (let i = 0; i < numPages; i++) {
            const stageRefKey = `stage${i}`;
            const stageRef = this.$refs[stageRefKey]?.[0]; // Safely access the stage reference

            if (stageRef) {
              const stage = stageRef.getNode();

              const image = new window.Image();
              image.src = noteTemplate; // Make sure this path is correct

              image.onload = () => {
                // Set the background image for each page
                this.pages[i].configBackground.fillPatternImage = image;

                // Redraw the stage to show the background
                stage.draw();
              };

              image.onerror = () => {
                // Handle error silently
              };
            }
          }
        });
      } else {
        // Load saved pages

        this.pages = Array.from({ length: savedPagesCount }, () => ({
          configBackground: {
            x: 0,
            y: 0,
            width: 720,
            height: 960,
            fillPatternImage: null,
            preventDefault: false,
          },
          configText: {
            x: 15,
            y: 10,
            text: this.drawNote?.text || "", // Use drawNote to set text
            fontSize: 14,
            lineHeight: 1.5,
            fill: "#3D556E",
          },
          lines: [],
        }));

        const numPages = savedPagesCount;

        this.$nextTick(() => {
          for (let i = 0; i < numPages; i++) {
            const stageRefKey = `stage${i}`;
            const stageRef = this.$refs[stageRefKey]?.[0]; // Safely access the stage reference

            if (stageRef) {
              const stage = stageRef.getNode();

              const image = new window.Image();
              image.src = noteTemplate; // Make sure this path is correct

              image.onload = () => {
                // Set the background image for each page
                this.pages[i].configBackground.fillPatternImage = image;

                // Redraw the stage to show the background
                stage.draw();
              };

              image.onerror = () => {
                // Handle error silently
              };
            }
          }
        });
      }
    },
    setDefaultValue() {
      this.configText.text = "";
      this.lines = [];
    },

    // Performance debugging methods
    enablePerformanceDebug() {
      this.performanceDebugMode = true;
      console.log('Performance debugging enabled. Use window.drawingPerformanceReport() to get metrics.');
      
      // Expose performance report globally
      if (typeof window !== 'undefined') {
        window.drawingPerformanceReport = () => {
          if (this.historyManager) {
            const metrics = this.historyManager.getPerformanceMetrics();
            console.log('Drawing Performance Metrics:', {
              ...metrics,
              fallbackHistorySize: this.fallbackHistoryLines?.length || 0,
              currentStrokeCount: this.historyManager.strokeCount,
              memoryUsageEstimate: this.estimateMemoryUsage()
            });
            return metrics;
          }
          return null;
        };
      }
    },

    estimateMemoryUsage() {
      // Rough estimate of memory usage
      let totalPoints = 0;
      if (this.lines && Array.isArray(this.lines)) {
        this.lines.forEach(page => {
          if (Array.isArray(page)) {
            page.forEach(line => {
              if (line && line.points) {
                totalPoints += line.points.length;
              }
            });
          }
        });
      }
      
      // Each point is roughly 16 bytes (2 numbers * 8 bytes each)
      // Plus overhead for objects and arrays
      const estimatedBytes = totalPoints * 16 * 1.5; // 1.5x for overhead
      return {
        totalPoints,
        estimatedBytes,
        estimatedKB: Math.round(estimatedBytes / 1024),
        estimatedMB: Math.round(estimatedBytes / (1024 * 1024) * 100) / 100
      };
    },

  },
  async mounted() {
    // Initialize drawing functionality from mixin
    this.initializeDrawing();

    eventBus.$on("setDefaultValuePaymentHistory", this.setDefaultValue);

    eventBus.$on("updateDrawNote", assignedDrawNote => {
      this.assignDrawNote(assignedDrawNote);
    });

    eventBus.$on("drawNoteUpdated", updatedDrawNote => {
      this.drawNoteInitialized = true;
      // Now you can safely access updated drawNote
      if (updatedDrawNote && updatedDrawNote.lines.length > 0) {
        this.$nextTick(async () => {
          await this.initializePages();
        });
      } else {
        this.initializePages();
      }
    });

    this.$nextTick(() => {
      if (!this.drawNoteInitialized) {
        this.$nextTick(async () => {
          await this.initializePages();
        });
      }
    });
    
    // Enable performance debugging in development
    if (process.env.NODE_ENV === 'development') {
      this.enablePerformanceDebug();
    }
  },

  beforeDestroy() {
    this.cleanupDrawing();
    
    // Clean up history manager
    if (this.historyManager) {
      this.historyManager.reset();
      this.historyManager = null;
    }
    
    // Clean up fallback history
    this.fallbackHistoryLines = [];
    this.fallbackHistoryStep = 0;
    
    // Clean up history properties
    this.historyLines = [];
    this.historyStep = 0;
    
    // Clean up global debug methods
    if (typeof window !== 'undefined') {
      delete window.drawingPerformanceReport;
    }

    // Clean up event listeners
    eventBus.$off("drawNoteUpdated");
    eventBus.$off("updateDrawNote");
    eventBus.$off("setDefaultValuePaymentHistory");

    // Clean up Konva stages
    Object.keys(this.$refs).forEach(refKey => {
      if (refKey.startsWith("stage")) {
        const vueStage = this.$refs[refKey][0];
        if (vueStage && vueStage.getStage) {
          const stage = vueStage.getStage();
          if (stage) {
            stage.height(1);
            stage.width(1);
            stage.destroy();
          }
        }
      }
    });
  },
};
</script>

<style>
.spanDot {
  background-color: #7e7e7e;
  border-radius: 50%;
  display: inline-block;
  display: flex;
  align-self: center;
}
#popoverThickness .popover-body {
  padding: 0px;
}
.sticky-parent {
  position: absolute;
  height: 100vmax;
  right: 0px;
}
.overflow-hidden {
  overflow: hidden;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5em;
  color: #333;
  z-index: 1000;
}

/* Floating page control panel styles */
.page-control-panel {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-number {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.page-buttons {
  display: flex;
  align-items: center;
  gap: 5px;
}

.page-control-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  padding: 0 !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.page-control-btn i {
  font-size: 12px;
}

.page-control-btn:hover {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

.add-page-btn:hover {
  background-color: #007bff !important;
  color: white !important;
}

.delete-page-btn:hover {
  background-color: #dc3545 !important;
  color: white !important;
}
</style>